import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error

    async function submit(e) {
        e.preventDefault();
        setStatus('sending');
        try {
            await axios.post(`${API}/contact`, form);
            setStatus('sent');
            setForm({ name: '', email: '', subject: '', message: '' });
        } catch (err) {
            console.error(err);
            setStatus('error');
        }
    }

    return (
        <div data-testid="section-contact" className="space-y-10">
            <div className="flex items-start justify-between">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">§VI — Correspondence</div>
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">Postmarked from Studio</div>
            </div>

            <h2 className="display text-[color:var(--ink)]" style={{ fontSize: 'clamp(40px, 5vw, 68px)' }}>
                Send us <span className="display-italic" style={{ color: 'var(--lavender-deep)' }}>a letter</span>.
                <br /> We do read them.
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
                <form onSubmit={submit} className="md:col-span-2 space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                        <Field label="Name" name="name" value={form.name} setForm={setForm} />
                        <Field label="Email" name="email" type="email" value={form.email} setForm={setForm} />
                    </div>
                    <Field label="Subject" name="subject" value={form.subject} setForm={setForm} />
                    <div>
                        <label className="mono text-[10px] text-[color:var(--moss-deep)]">Message</label>
                        <textarea
                            data-testid="contact-message"
                            required
                            rows={5}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            className="w-full paper-lines bg-transparent border-b border-[color:var(--moss-deep)] outline-none py-2 text-[color:var(--ink)] resize-none placeholder:text-[color:var(--moss-deep)]/50"
                            placeholder="Dear folio…"
                        />
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            data-testid="contact-submit"
                            disabled={status === 'sending'}
                            className="btn-lavender px-5 py-2.5 rounded-sm text-xs mono flex items-center gap-2"
                        >
                            <Send size={12} />
                            {status === 'sending' ? 'Sealing…' : 'Post the letter'}
                        </button>
                        {status === 'sent' && (
                            <motion.span
                                initial={{ opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="mono text-[10px] text-[color:var(--lavender-deep)]"
                            >
                                ✦ Delivered. We'll write back.
                            </motion.span>
                        )}
                        {status === 'error' && (
                            <span className="mono text-[10px] text-red-600">Failed. Try again.</span>
                        )}
                    </div>
                </form>

                <aside className="space-y-6">
                    <div>
                        <div className="mono text-[10px] text-[color:var(--moss-deep)]">STUDIO</div>
                        <div className="display text-[color:var(--ink)] text-2xl mt-1">Folio &amp; Co.</div>
                        <p className="text-[13px] text-[color:var(--ink-soft)] mt-2 leading-relaxed">
                            22 Linden Row<br />
                            Second Floor, Studio C<br />
                            Bengaluru · 560001
                        </p>
                    </div>
                    <div>
                        <div className="mono text-[10px] text-[color:var(--moss-deep)]">OFFICE HOURS</div>
                        <p className="text-[13px] text-[color:var(--ink-soft)] mt-2 leading-relaxed">
                            Mon — Fri, 09:00 — 18:00 IST<br />
                            Reply within one working day.
                        </p>
                    </div>
                    <div className="border-l-2 border-[color:var(--lavender-deep)] pl-3">
                        <p className="display-italic text-[color:var(--ink)] text-[15px]">
                            "A letter is a private thing.<br /> We treat it as such."
                        </p>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Field({ label, name, type = 'text', value, setForm }) {
    return (
        <div>
            <label className="mono text-[10px] text-[color:var(--moss-deep)]">{label}</label>
            <input
                required={name !== 'subject'}
                type={type}
                data-testid={`contact-${name}`}
                value={value}
                onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
                className="w-full bg-transparent border-b border-[color:var(--moss-deep)] outline-none py-2 text-[color:var(--ink)]"
            />
        </div>
    );
}
